import { formatLocale } from 'd3-format';
import { get, merge, set } from 'lodash';

import { types as eventTypes } from './enums/event.js';
import { FibToolLabelFormat } from './enums/tools/fibonacci.js';
import Handle from './tools/Handle.js';
import createUid from './util/uid.js';
import zoomUtil from './util/zoom.js';

/**
 * Tools are customizable chart widgets overlaid on the chart and sharing its coordinate
 * space (on a separate canvas element)
 */
export default class Tool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {Object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		/** @type {ModchartsInteractive} */
		this.core = core;
		/** @type {Object} */
		this.params = this.getParams(settings);
	}

	/**
	 * Create new tool handle
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 * @param {Handle} HandleClass - Instance of tool handle class
	 * @return {Handle} New instance of given HandleClass
	 */
	createHandle(mousePanel, HandleClass) {
		const idx = Math.round(this.panel.xAxis.primaryScale.invert(mousePanel[0]));
		const valIn = this.panel.yAxis.primaryScale.invert(mousePanel[1]);
		let value = 0;
		const date = this.core.exchangeDates[idx];

		// allow different handle types to be passed
		HandleClass = HandleClass || Handle;

		// always store handle values as actuals (not normalized)
		if (this.panel.isNormalized) {
			value = this.panel.normalizedToActual(valIn).toFixed(4);
		} else {
			value = valIn.toFixed(4);
		}

		return new HandleClass(this, {
			date: new Date(date).getTime(),
			value: Number(value)
		});
	}

	/**
	 * Drag both handles at once based on mouse position
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 */
	dragTool(mousePanel) {
		if (
			!this.core.state.dragTool ||
			!this.state.dragToolOrigin ||
			!this.state.handleOrigin
		) {
			return;
		}

		const diffTool = [
			mousePanel[0] - this.state.dragToolOrigin[0],
			mousePanel[1] - this.state.dragToolOrigin[1]
		];
		const coordsLeft = this.state.handleOrigin[0];
		const coordsRight = this.state.handleOrigin[1];
		const newCoordsLeft = [
			coordsLeft[0] + diffTool[0],
			coordsLeft[1] + diffTool[1]
		];
		const newCoordsRight = [
			coordsRight[0] + diffTool[0],
			coordsRight[1] + diffTool[1]
		];
		const dateIdxLeft = Math.round(
			this.panel.xAxis.primaryScale.invert(newCoordsLeft[0])
		);
		const dateIdxRight = Math.round(
			this.panel.xAxis.primaryScale.invert(newCoordsRight[0])
		);
		let valLeft = this.panel.yAxis.primaryScale.invert(newCoordsLeft[1]);
		let valRight = this.panel.yAxis.primaryScale.invert(newCoordsRight[1]);

		// check for tool dragged beyond the panel's domain boundaries
		if (
			dateIdxRight > this.core.exchangeDates.length - 1 ||
			dateIdxRight < 0 ||
			dateIdxLeft > this.core.exchangeDates.length - 1 ||
			dateIdxLeft < 0
		) {
			// reset drag/handle origins, makes UX feel less sticky
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];

			// don't update handles
			return;
		}

		// always store handle values as actuals
		if (this.panel.isNormalized) {
			valLeft = this.panel.normalizedToActual(valLeft);
			valRight = this.panel.normalizedToActual(valRight);
		}

		// update handles with the new dates and values
		this.handle[0].params.date = new Date(
			this.core.exchangeDates[dateIdxLeft]
		);
		this.handle[1].params.date = new Date(
			this.core.exchangeDates[dateIdxRight]
		);
		this.handle[0].params.value = valLeft;
		this.handle[1].params.value = valRight;
	}

	/**
	 * Calculate the closest point to the mouse using linear interpolation
	 * @param {number} x0 - X-coordinate of first point
	 * @param {number} y0 - Y-coordinate of first point
	 * @param {number} x1 - X-coordinate of second point
	 * @param {number} y1 - Y-coordinate of second point
	 * @param {number} mouseX - X-coordinate of mouse cursor
	 * @param {number} mouseY - Y-coordinate of mouse cursor
	 * @return {number[]} - Coordinates for closest point
	 */
	_getClosestPoint(x0, y0, x1, y1, mouseX, mouseY) {
		const dx = x1 - x0;
		const dy = y1 - y0;
		const t = ((mouseX - x0) * dx + (mouseY - y0) * dy) / (dx * dx + dy * dy);
		const lineX = this._lerp(x0, x1, t);
		const lineY = this._lerp(y0, y1, t);

		return [lineX, lineY];
	}

	/**
	 * Get data associated with X-coordinate
	 * @param {Number} valX - X-coordinate for which to get associated data
	 * @return {Object} Data associated with X-coordinate
	 */
	getDataPointFromX(valX) {
		const domainIndex = this.getDataPointIndexFromX(valX);
		return this.core.dataPrimary[domainIndex];
	}

	/**
	 * Get domain index associated with X-coordinate
	 * @param {Number} valX - X-coordinate for which to get associated domain index
	 * @return {Number} - Domain index associated with X-coordinate
	 */
	getDataPointIndexFromX(valX) {
		const valIndex = this.panel.xAxis.primaryScale.invert(valX);
		return this.core._getIndexByDomain(valIndex, this.core.dataPrimary, 'dateIndex');
	}

	/**
	 * Retrieves default parameters for tool, including details and styles
	 * @return {object} Object containing default tool parameters
	 * @abstract
	 */
	_getDefaultParams() {
		return {};
	}

	/**
	 * Get formatted value with the specified options for Fibonacci tool
	 * @param {Number} val - Value to format
	 * @return {String} Formatted value
	 */
	_getFibFormatValue(val) {
		const formatString = this.params.labelFormat === FibToolLabelFormat.PERCENT
			? this.params.style.label.format.percent
			: this.params.style.label.format.default;

		const valOut = formatLocale(this.core.locale).format(formatString)(val);

		// Use B for billion instead of G for giga
		return valOut.replace(/G/, 'B');
	}

	/**
	 * Get merged params object
	 * @param {Object} settings - Parameters for tool
	 * @return {Object} Merged params object
	 */
	getParams(settings) {
		const params = {
			name: 'Chart Tool',
			id: 'tool',
			uid: createUid(),
			style: {}
		};

		return merge({}, params, this._getDefaultParams(), settings);
	}

	/**
	 * Get tool constructor for supplied tool type/id
	 * @param {String} type - Short name for tool
	 * @param {Object} toolMap - List of tool classes that can be added to the chart
	 * @return {Object} Tool class associated with given short name
	 */
	static getToolFromType(type, toolMap) {
		return toolMap[type];
	}

	/**
	 * Determine if the tool is immediately created when tool button is clicked
	 * @type {Boolean}
	 */
	get isImmediate() { return this.params.isImmediate; }

	/**
	 * Set the isImmediate property of the tool
	 * @param {Boolean} value - Whether or not tool is immediately created
	 * @type {Boolean}
	 */
	set isImmediate(value) { this.params.isImmediate = value; }

	/**
	 * Initialize the tool
	 * @param {Panel} panel - Parent panel for the tool
	 * @param {Handle[]} handles - Array containing tool handle objects
	 */
	init(panel, handles = []) {
		/** @type {Panel} */
		this.panel = panel;
		/** @type {Handle[]} */
		this.handle = handles;
		this.path = [];

		/** @type {Object} */
		this.state = {
			dragHandle: null,
			hover: false,
			inProgressHandle: false,
			selected: false,
			visible: true
		};
		/** @type {Object} */
		this.flags = {};
	}

	/**
	 * Check for overlap between two boxes
	 * @param {Object} r1 - Info about first box
	 * @param {Object} r2 - Info about second box
	 * @return {Boolean} Whether or not the two boxes overlap
	 */
	intersect(r1, r2) {
		// todo: performance improvement - convert to simple arrays instead of objects with x,y,width,height props
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		}
		return false;
	}

	/**
	 * Determines if mouse is within tolerance area of tool
	 * @return {Boolean} Whether or not tool is within tolerance area of tool
	 * @abstract
	 */
	isWithin() {
		return false;
	}

	/**
	 * Determines if mouse is within tool handle
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 * @return {Boolean} Whether or not mouse is within tool handle
	 */
	isWithinHandle(mousePanel) {
		for (const handle of this.handle) {
			if (handle.isWithin(mousePanel)) return handle;
		}

		return false;
	}

	/**
	 * Perform linear interpolation
	 * @param {number} a - Beginning coordinate
	 * @param {number} b - Ending coordinate
	 * @param {number} x - Parameter representing distance between coordinates
	 * @return {number} - Interpolated coordinate
	 */
	_lerp(a, b, x) {
		return a + x * (b - a);
	}

	/**
	 * Handle click on canvas for this tool
	 * @abstract
	 */
	onClickCanvas() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Mousedown on handle
	 * @abstract
	 */
	onClickHandle() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Mousedown on tool itself
	 * @abstract
	 */
	onClickTool() {
		throw new Error('Required Tool method not found');
	}

	/** Should be called by a tool when it is finished drawing and no longer active */
	onComplete() {
		if (this.state) {
			// reset state variables
			this.state.dragHandle = null;
			this.state.dragToolOrigin = null;
			this.state.handleOrigin = null;
			this.state.inProgressHandle = false;
			this.storePrevHandles();
		}
		if (this.core) {
			this.core.state.dragTool = null;
			this.core.state.tool = null;
			if (this.core.setToolMode) this.core.setToolMode(null);
		}
	}

	/**
	 * Mousedown handler.  select the tool and/or begin dragging
	 * @param {Number[]} mouse - Array of mouse coordinates
	 * @param {String} touchId - Identifier associated with the current touch event
	 */
	onMousedown(mouse, touchId) {
		// loop through all handles to see if it was a mousedown on a handle or tool body
		// if so set this tool's draghandle to handle and select this tool

		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		const handleWithin = this.isWithinHandle(mousePanel, touchId);
		const toolWithin = this.isWithin(mousePanel);
		const core = this.core;

		// if within a handle, begin the handle dragging process
		if (handleWithin) {
			this.state.inProgressHandle = true;
			this.state.dragHandle = handleWithin;
			core.state.dragTool = null;
		} else if (toolWithin && this.params.numberOfAnchors === 1) {
			this.state.dragHandle = this.handle[0];
			this.state.inProgressHandle = true;
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];
			core.state.dragTool = this;
		} else if (toolWithin) {
			this.state.dragHandle = null;
			this.state.inProgressHandle = null;
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];
			core.state.dragTool = this;
		}

		// select the tool (and unselect others)
		if (handleWithin || toolWithin) {
			zoomUtil.unregisterZoom(core); // prevent zoom event from firing
			core.setToolMode(this.params.id);
			if (core.selectTool) core.selectTool(this);

			// update handle colors immediately rather than waiting for mousemove
			this.panel.clearTools();
			this.panel.renderTools();

			core.onToolMousedown(this);

			return true;
		}
	}

	/**
	 * Handle mouse move event for this tool
	 * @abstract
	 */
	onMousemove() {
		throw new Error('Required Tool method not found');
	}

	/** Mouseup handler. Clears out drag states. */
	onMouseup() {
		this.core.onToolMouseup(this);

		this.state.dragHandle = null;
		this.state.dragToolOrigin = null;
		this.state.handleOrigin = null;
		this.state.inProgressHandle = false;
		this.core.state.dragTool = null;
		this.core.state.tool = null;

		this.onComplete();
	}

	/** Removes tool reference from panel's tools collection */
	remove() {
		const chart = this.core;
		this.onComplete();

		Object.keys(this.panel.tools).forEach(idx => {
			const el = this.panel.tools[idx];
			if (el === this) {
				this.panel.tools.splice(idx, 1);
				return false;
			}
		});

		// remove any associated flags
		const flags = this.flags || {};
		Object.keys(flags).forEach(key => {
			const flag = flags[key];
			flag.remove();
			this.flags[key] = null;
		});

		chart.clearPanels();
		chart.render();

		chart.eventEmitter.emit(eventTypes.TOOL_REMOVE, {
			toolType: this.params && this.params.id
		});
	}

	/** Draws the tool on the canvas */
	render() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Sets a style on this tool
	 * @param {string} name - The style to set
	 * @param {string} value - The new style value
	 */
	setStyle(name, value) {
		if (this.getStyle(name) !== undefined) {
			let updateStyle = false;

			// Make sure that numbers are saved as numbers, not strings
			if (typeof this.getStyle(name) === 'number') {
				// Only replace number type with new number
				if (typeof value !== 'number' && !isNaN(value)) {
					updateStyle = true;
					value = Number(value);
				} else {
					this.core.warn('Tool.setStyle: Setting a non-numeric value where a numeric value is required is not allowed');
				}
			} else {
				updateStyle = true;
			}

			if (updateStyle) set(this.params.style, name, value);
		} else {
			this.core.warn(`Style ${name} not found`);
		}
	}

	/**
	 * Returns style info for the given selector from this tool's params.style collection
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		return get(this.params.style, selector);
	}

	/**
	 * Determines if the tool should be able to snap to data
	 * @type {Boolean}
	 */
	get shouldSnap() { return true; }

	/**
	 * Toggle the visibility of the tool handles
	 * @param {Boolean} isVisible Whether of not the handles should be visible
	 */
	toggleHandles(isVisible) {
		for (let h = 0; h < this.handle.length; h++) {
			this.handle[h].state.visible = isVisible;
		}
	}

	/**
	 * Creates generic object representation of tool
	 * @return {object} Generic object representation of tool
	 */
	toJSON() {
		const handles = this.handle || [];
		return {
			params: { ...this.params },
			handle: handles.map(handle => handle.toJSON())
		};
	}

	// save handle positions to the tool's state, in case we want to revert to them later
	storePrevHandles() {
		if (
			this.handle?.length &&
			this.handle.length === 2 &&
			this.handle[0].params &&
			this.handle[1].params
		) {
			this.state.prevHandles = [
				{
					date: this.handle[0].params.date,
					value: this.handle[0].params.value
				},
				{
					date: this.handle[1].params.date,
					value: this.handle[1].params.value
				}
			];
		}
	}

	revertHandles() {
		const prevHandles = this.state.prevHandles;
		if (prevHandles && this.handle.length === 2) {
			this.handle[0].params.date = prevHandles[0].date;
			this.handle[0].params.value = prevHandles[0].value;
			this.handle[1].params.date = prevHandles[1].date;
			this.handle[1].params.value = prevHandles[1].value;
		}

		// re-render tool at previous location
		this.panel.clearTools();
		this.panel.renderTools();

		this.onComplete();
	}
}
